import { log } from "@/cli/log";
import chalk from "chalk";
import { readFileSync, existsSync } from "node:fs";
import { getBaseUrl, DEFAULT_ROUTING_PROMPT } from "@/config";
import { verbose } from "@/cli/dev";
import { writeFile } from "node:fs/promises";
import { renderShifts } from "@/domain/scheduler";
import type Table from "cli-table3";
import type { Member, Rotation } from "@/domain/schema";
import {
  BeepsConfigurationSchema,
  type BeepsConfiguration,
} from "@/domain/beeps-json-schema";
import type { ApiRotationsResponse } from "@/pages/api/cli/rotations";

// Define extended rotation type with invitations
type ExtendedRotation = Rotation & {
  members: Member[];
  invitations?: Array<{ email: string; status: string }>;
};

export type { BeepsConfiguration };
export { BeepsConfigurationSchema };

const CONFIG_FILE_PATH = "beeps.json";

export function readConfigurationFile(): BeepsConfiguration | null {
  try {
    if (!existsSync(CONFIG_FILE_PATH)) {
      return null;
    }
    const data = readFileSync(CONFIG_FILE_PATH, "utf-8");
    const parsed = JSON.parse(data);

    // Handle legacy format (single rotation without name)
    if (parsed.members && !parsed.rotations) {
      return BeepsConfigurationSchema.parse({
        rotations: [
          {
            name: "default",
            members: parsed.members,
            frequency: parsed.frequency,
            startDate: parsed.startDate,
          },
        ],
      });
    }

    return BeepsConfigurationSchema.parse(parsed);
  } catch (error) {
    if (error instanceof Error) {
      log.error(`Failed to read configuration file: ${error.message}`);
      verbose && console.error("Detailed error:", error);
    } else {
      log.error("Failed to read configuration file");
      verbose && console.error("Detailed error:", error);
    }
    return null;
  }
}

export async function hasRotationsConfigFile() {
  return existsSync(CONFIG_FILE_PATH);
}

export async function writeDefaultRotationsConfigFile({
  emails,
}: {
  emails: string[];
}): Promise<void> {
  try {
    await writeFile(
      CONFIG_FILE_PATH,
      JSON.stringify(
        {
          routingPrompt: DEFAULT_ROUTING_PROMPT,
          rotations: [
            {
              name: "default",
              members: emails,
              startDate: new Date().toISOString().split("T")[0]!,
              frequency: "daily",
            },
          ],
        } satisfies BeepsConfiguration,
        null,
        2,
      ),
    );
  } catch (error) {
    if (error instanceof Error) {
      log.error(`Failed to write configuration file: ${error.message}`);
      verbose && console.error("Detailed error:", error);
    } else {
      log.error("Failed to write configuration file");
      verbose && console.error("Detailed error:", error);
    }
    throw new Error("Failed to write rotation configuration");
  }
}

export async function pushConfiguration(
  token: string,
  userId: string,
  config: BeepsConfiguration,
) {
  try {
    const response = await fetch(
      `${getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT })}/api/cli/push-configuration`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      },
    );

    if (!response.ok) {
      if (response.status === 400) {
        const { error } = await response.json();
        throw new Error(`Invalid configuration: ${JSON.stringify(error)}`);
      }
      throw new Error(`Failed to push configuration: ${response.statusText}`);
    }
  } catch (error) {
    if (error instanceof Error) {
      log.error(`Failed to push configuration: ${error.message}`);
      verbose && console.error("Detailed error:", error);
    } else {
      log.error("Failed to push configuration");
      verbose && console.error("Detailed error:", error);
    }
    throw error; // Re-throw to maintain existing error handling flow
  }
}

export async function renderRotation({
  pendingRotation,
  activeRotation,
}: {
  pendingRotation?: ExtendedRotation;
  activeRotation?: ExtendedRotation;
}) {
  const Table = (await import("cli-table3")).default;

  async function renderRotationSection(
    rotation: ExtendedRotation,
    isActive: boolean,
  ) {
    if (!rotation) return;

    // Use consistent emoji and color based on state
    const stateEmoji = isActive ? "🟢" : "⏳";
    const stateColor = isActive ? chalk.green : chalk.yellow;
    const stateText = isActive ? "Active" : "Pending";

    // Single-line status header
    log.info(
      `${stateEmoji} ${stateColor(`${stateText}`)} (started ${new Date(rotation.startDate).toISOString().split("T")[0]})`,
    );

    // Combined table showing members and invitations
    const table = new Table({
      head: ["Engineer", "Status", "Notifications"],
      style: { head: ["blue"] },
    });

    // Add accepted members
    rotation.members?.forEach((member: any) => {
      // Simplified notification logic: focus on showing email verification status
      const notifications =
        [
          // Show checkmark for verified email
          member.emailVerified ? "✓ Email" : "",
          // Keep phone logic intact
          member.phoneEnabled
            ? member.phoneVerified
              ? "✓ SMS"
              : "⚠ SMS"
            : "",
        ]
          .filter(Boolean)
          .join(", ") || "None";

      // Enhanced status display logic prioritizing emailVerified field
      // over status field for better display consistency
      let status;
      if (member.emailVerified || member.userId) {
        // If email is verified or user ID is set, show as Active
        status = chalk.green("Active");
      } else if (member.status === "invited") {
        status = chalk.yellow("Invited");
      } else if (member.status === "pending") {
        status = chalk.yellow("Pending");
      } else if (member.status === "declined") {
        status = chalk.red("Declined");
      } else {
        status = chalk.gray(member.status);
      }

      // Simplified: Use notifications value, or "X" for invited members with unverified emails
      const displayNotifications =
        member.status === "invited" && !member.emailVerified && !member.userId
          ? "X"
          : notifications;

      table.push([chalk.white(member.email), status, displayNotifications]);
    });

    // Add pending invitations
    rotation.invitations
      ?.filter((invite: any) => invite.status !== "active")
      .forEach((invite: any) => {
        table.push([
          chalk.white(invite.email),
          chalk.yellow(invite.status),
          "Pending",
        ]);
      });

    // Add shift preview if rotation has members
    await renderShiftPreview(rotation, table);

    if (table.length > 0) {
      console.log(table.toString());
    } else {
      log.info("No members or pending invitations");
    }
  }

  // Show active rotation first
  if (activeRotation) {
    await renderRotationSection(activeRotation, true);
  }

  // Show pending rotation with transition indicator
  if (pendingRotation) {
    if (activeRotation) {
      log.info(
        "\n" +
          chalk.yellow(
            "↓ Will replace active rotation when all invitations are accepted ↓",
          ),
      );
    }
    await renderRotationSection(pendingRotation, false);
  }
}

async function renderShiftPreview(
  rotation: ExtendedRotation,
  table: Table.Table,
) {
  if (!rotation.members?.length) {
    return; // No members to generate shifts for
  }

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0]!;

  // Create shiftables from rotation members
  const shiftables = rotation.members.map((member: any) => ({
    id: member.email, // Use email as ID since it's displayed in the table
  }));

  // Generate 4 shifts from today
  const startDate = new Date(rotation.startDate).toISOString().split("T")[0]!;
  const shifts = renderShifts(
    shiftables,
    {
      rotationType: rotation.frequency,
      sortOrder: { type: "id" },
      startDate,
    },
    {
      fromDate: today,
      shiftsCount: 4,
    },
  );

  // Add shift preview rows
  table.push([chalk.blue("Upcoming Shifts"), "", ""]);
  shifts.forEach((shift) => {
    table.push([
      chalk.dim(`${shift.startDate} → ${shift.endDate}`),
      chalk.dim(shift.shiftableId),
      chalk.dim("Scheduled"),
    ]);
  });
}

export async function displayRotations(rotationsData: ApiRotationsResponse) {
  try {
    // Display active rotations
    if (rotationsData.active.length > 0) {
      log.info("\nActive Rotations:");
      for (const rotation of rotationsData.active) {
        log.info(`\n${chalk.blue(`Rotation: ${rotation.name}`)}`);
        await renderRotation({
          activeRotation: rotation,
        });
      }
    }

    // Display pending rotations
    if (rotationsData.pending.length > 0) {
      log.info("\nPending Rotations:");
      for (const rotation of rotationsData.pending) {
        log.info(`\n${chalk.blue(`Rotation: ${rotation.name}`)}`);
        await renderRotation({
          pendingRotation: rotation,
        });
      }
    }

    if (!rotationsData.active.length && !rotationsData.pending.length) {
      log.info("\nNo rotations found");
      // Add wait when no rotations exist
      verbose && console.log("No rotations found, waiting 3 seconds...");
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }

    // Only show success message if there are any pending rotations
    if (rotationsData.pending.length > 0) {
      log.success(
        "\n✉️  We've sent invitations to the selected engineers. The rotation will be activated once they verify their notification preferences.",
      );
    }

    // Display webhook URL
    const baseUrl = getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT });

    // Debug the webhook token

    // Ensure the token is appended to the webhook URL
    if (rotationsData.webhookToken) {
      log.info(
        `\nUse this URL to send alerts: ${baseUrl}/api/webhooks/${rotationsData.webhookToken}`,
      );
    } else {
      log.info(`\nUse this URL to send alerts: ${baseUrl}/api/webhooks/`);
      log.warning("No webhook token found.");
    }
  } catch (error) {
    console.error("Error displaying rotations:", error);
    process.exit(1);
  }

  // Exit after displaying rotations
  process.exit(0);
}

export async function fetchRotations(token: string) {
  try {
    // Fetch rotations from Astro endpoint
    const rotationsResponse = await fetch(
      `${getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT })}/api/cli/rotations`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!rotationsResponse.ok) {
      throw new Error(
        `Failed to fetch rotations: ${rotationsResponse.statusText}`,
      );
    }

    const rotationsData =
      (await rotationsResponse.json()) as ApiRotationsResponse;
    return displayRotations(rotationsData);
  } catch (error) {
    log.error("Failed to fetch rotations");
    if (error instanceof Error) {
      console.error(error.message);
    } else {
      console.error(error);
    }
    process.exit(1);
  }
}
